/**
 * Календарь для выбора даты — чистая арифметика по строкам «2026-10-13», без
 * `Date`: сетка месяца не зависит от часового пояса и перехода на летнее
 * время, а значения совпадают с тем, что ждут поля дат (`isoDay`).
 */

export interface MonthRef {
  year: number
  /** 1–12. */
  month: number
}

export interface CalendarCell {
  /** «2026-10-13». */
  iso: string
  day: number
  /** Ячейка из соседнего месяца — её рисуют бледной. */
  inMonth: boolean
}

export const MONTHS_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const

/** Неделя с понедельника — как в календарях Узбекистана и России. */
export const WEEKDAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

const pad = (n: number, size = 2) => String(n).padStart(size, '0')

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

export function toIso(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`
}

/** «2026-10-13» → части; несуществующая дата или мусор — null. */
export function splitIso(iso: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null
  return { year, month, day }
}

/** Номер дня от 1970-01-01 (алгоритм Хиннанта «days from civil»). */
function dayNumber(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year
  const era = Math.floor(y / 400)
  const yoe = y - era * 400
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy
  return era * 146097 + doe - 719468
}

function fromDayNumber(n: number): string {
  const z = n + 719468
  const era = Math.floor(z / 146097)
  const doe = z - era * 146097
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  )
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100))
  const mp = Math.floor((5 * doy + 2) / 153)
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1
  const month = mp < 10 ? mp + 3 : mp - 9
  return toIso(yoe + era * 400 + (month <= 2 ? 1 : 0), month, day)
}

/** День недели с понедельника: 0 — понедельник, 6 — воскресенье. */
export function weekdayIndex(year: number, month: number, day: number): number {
  // 1970-01-01 — четверг (3 от понедельника).
  return (((dayNumber(year, month, day) + 3) % 7) + 7) % 7
}

/** Сдвиг даты на дни; неверная дата остаётся как есть. */
export function addDaysIso(iso: string, days: number): string {
  const parts = splitIso(iso)
  if (!parts) return iso
  return fromDayNumber(dayNumber(parts.year, parts.month, parts.day) + days)
}

export function shiftMonth(ref: MonthRef, delta: number): MonthRef {
  const index = ref.year * 12 + (ref.month - 1) + delta
  return { year: Math.floor(index / 12), month: (index % 12) + 1 }
}

/** Тот же день в соседнем месяце; 31-е в коротком месяце становится последним днём. */
export function addMonthsIso(iso: string, delta: number): string {
  const parts = splitIso(iso)
  if (!parts) return iso
  const { year, month } = shiftMonth(parts, delta)
  return toIso(year, month, Math.min(parts.day, daysInMonth(year, month)))
}

/** Шесть недель месяца с понедельника — сетка не прыгает по высоте от месяца к месяцу. */
export function monthGrid(ref: MonthRef): CalendarCell[] {
  const first = toIso(ref.year, ref.month, 1)
  const start = addDaysIso(first, -weekdayIndex(ref.year, ref.month, 1))
  return Array.from({ length: 42 }, (_, i) => {
    const iso = addDaysIso(start, i)
    const parts = splitIso(iso) as { year: number; month: number; day: number }
    return { iso, day: parts.day, inMonth: parts.month === ref.month && parts.year === ref.year }
  })
}

/** «2026-10-13» → «13.10.2026»; пусто или мусор — пустая строка. */
export function formatIsoRu(iso: string): string {
  const parts = splitIso(iso)
  return parts ? `${pad(parts.day)}.${pad(parts.month)}.${pad(parts.year, 4)}` : ''
}

/**
 * Дата, набранная руками: «13.10.2026», «13/10/2026», «13-10-26», «1.2.2027».
 * Две цифры года — двадцать первый век. Несуществующая дата — null.
 */
export function parseRuDate(text: string): string | null {
  const match = /^\s*(\d{1,2})[./\-\s](\d{1,2})[./\-\s](\d{2}|\d{4})\s*$/.exec(text)
  if (!match) return null
  const year = match[3]!.length === 2 ? 2000 + Number(match[3]) : Number(match[3])
  const iso = toIso(year, Number(match[2]), Number(match[1]))
  return splitIso(iso) ? iso : null
}

/**
 * Время, набранное руками: «9», «930», «09:30», «9.30», «21 05». Часы 0–23,
 * минуты 0–59; иначе null.
 */
export function parseTime(text: string): string | null {
  const value = text.trim()
  const match =
    /^(\d{1,2})[:.\s](\d{1,2})$/.exec(value) ??
    /^(\d{1,2})(\d{2})$/.exec(value) ??
    /^(\d{1,2})()$/.exec(value)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2] || 0)
  if (hours > 23 || minutes > 59) return null
  return `${pad(hours)}:${pad(minutes)}`
}

/** Варианты времени через шаг в минутах: «00:00», «00:30», … — для списка выбора. */
export function timeSlots(stepMinutes: number): string[] {
  return Array.from({ length: Math.ceil((24 * 60) / stepMinutes) }, (_, i) => {
    const total = i * stepMinutes
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
  })
}
